import { notifications } from '@mantine/notifications';
import { DiscountCodeAvaliableQueryResponse, DiscountCodeType, MenuItemDto } from '@models/ropolo';

import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { safeString } from '@utils/stringUtil';
import { AxiosError } from 'axios';
import { v4 as uuidV4 } from 'uuid';
import {
  GetDiscountParams,
  changeDiscountActivationPayload,
  createDiscountCodePayload,
} from '../models/payload/discount';
import { DiscountResponse, DiscountsResponse } from '../models/response/discount';
import { IError, IErrorResponse } from '../models/response/error';
import {
  changeDiscountActivation,
  checkDiscountCodeSite,
  createDiscount,
  getAllDiscount,
  getDiscountByIdService,
} from '../services/discount';
// eslint-disable-next-line import/no-cycle
import { createAppAsyncThunk } from '../utils/reduxUtil';
import cartSlice, { CartItem } from './cart';

interface DiscountCodeState {
  items: DiscountsResponse | undefined;
  loading: boolean;
  pageNumber: number;
  pageSize: number;
  search: string;
  discountInfo: DiscountResponse | undefined;
  showAddModal: boolean;
  errors: IError | undefined;
}

const initialState: DiscountCodeState = {
  items: undefined,
  loading: false,
  pageNumber: 1,
  pageSize: 10,
  search: '',
  discountInfo: undefined,
  showAddModal: false,
  errors: undefined,
};

interface UseDiscountCodeInput {
  code: string;
  vendorId: string;
  cartItems: CartItem[];
}

interface UseDiscountCodeResponse {
  status: string;
  data: {
    discountCode: DiscountCodeAvaliableQueryResponse | null;
  };
}

const getAllDiscountCode = createAppAsyncThunk(
  'discountCode/getDiscountCodes',
  async (params: GetDiscountParams): Promise<DiscountsResponse> => {
    const response = await getAllDiscount(params);
    return response;
  }
);

const getDiscountById = createAppAsyncThunk(
  'discountCode/getDiscountCodeById',
  async (id: string): Promise<DiscountResponse> => {
    const response = await getDiscountByIdService(id);
    return response;
  }
);

const createDiscountCode = createAppAsyncThunk<void, createDiscountCodePayload>(
  'discountCode/createDiscountCode',
  async (input, { dispatch, rejectWithValue, getState }) => {
    try {
      const { pageNumber, pageSize, search } = getState().discountCode;
      const response = await createDiscount(input);
      dispatch(
        getAllDiscountCode({
          PageNumber: pageNumber,
          PageSize: pageSize,
          Search: search,
        })
      );
      return response;
    } catch (error) {
      return rejectWithValue((error as AxiosError).response?.data as IErrorResponse);
    }
  }
);

const changeActivateDiscountCode = createAppAsyncThunk<void, changeDiscountActivationPayload>(
  'discountCode/changeActivateDiscountCode',
  async (payload, { dispatch, getState }) => {
    const { pageNumber, pageSize, search } = getState().discountCode;
    const response = await changeDiscountActivation(payload);

    dispatch(
      getAllDiscountCode({
        PageNumber: pageNumber,
        PageSize: pageSize,
        Search: search,
      })
    );
    return response;
  }
);

const getDiscountCode = createAppAsyncThunk<UseDiscountCodeResponse, UseDiscountCodeInput>(
  'discountCode/getDiscountCode',
  async ({ code, cartItems }, { dispatch }) => {
    try {
      const response = await checkDiscountCodeSite(code);
      const discountCode = response.data;
      if (discountCode) {
        dispatch(
          cartSlice.actions.setDiscountCode({
            id: safeString(discountCode.id),
            usageCount: discountCode.usageCount || 0,
            expiration: new Date(safeString(discountCode.expiration)).toDateString(),
            type: safeString(discountCode.type) as DiscountCodeType,
            value: discountCode.value || 0,
            cap: discountCode.cap || 0,
            code: safeString(discountCode.code),
            active: discountCode.active || false,
            usageLimitPerUser: discountCode.usageLimitPerUser || 0,
            vendorDeal: discountCode.vendorDeal || 0,
            // @ts-ignore
            freeItems:
              discountCode.freeItems?.map((fI) => ({
                id: fI.id,
                title: safeString(fI.title),
                price: fI?.price || 0,
                discount: fI.discount,
                packagingCost: fI.packagingCost,
                withTax: fI.withTax,
                servePackagingCost: fI.servePackagingCost,
              })) || [],
          })
        );

        if (discountCode.type === 'FREEITEM') {
          discountCode.freeItems?.map((freeItem: MenuItemDto) => {
            const isItemExistInCart = cartItems.find((cartItem: CartItem) => cartItem.id === freeItem.id);

            if (isItemExistInCart) {
              return dispatch(
                cartSlice.actions.updateOrderItem({
                  temporaryOrderId: isItemExistInCart.temporaryOrderId,
                  count: isItemExistInCart.count + 1,
                })
              );
            }

            return dispatch(
              cartSlice.actions.createOrder({
                ...freeItem,
                count: 1,
                temporaryOrderId: uuidV4(),
                orderItemToppings: [],
                toppingCategories: [],
              })
            );
          });
        }

        notifications.show({
          title: 'موفق',
          message: 'کد تخفیف اعمال شد.',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'خطا',
          message: 'کد تخفیف صحیح نیست یا به سقف تعداد استفاده مجاز رسیده است.',
          color: 'red',
        });
      }
      return response.data;
    } catch (error: any) {
      const res = error.response;
      return res ? res.data : '';
    }
  }
);

const discountCodeSlice = createSlice({
  name: 'discountCode',
  initialState,
  reducers: {
    setPageNumber: (state, action) => ({
      ...state,
      pageNumber: action.payload,
    }),
    setPageSize: (state, action) => ({
      ...state,
      pageSize: action.payload,
    }),
    setFilter: (state, action) => ({
      ...state,
      search: action.payload,
    }),
    toggleShowAdd: (state, action) => ({
      ...state,
      errors: undefined,
      showAddModal: action.payload,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<DiscountCodeState>) => {
    builder.addCase(getAllDiscountCode.pending, (state) => ({ ...state, loading: true, items: undefined }));

    builder.addCase(getAllDiscountCode.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      items: action.payload,
    }));

    builder.addCase(getDiscountById.pending, (state) => ({ ...state, loading: true, discountInfo: undefined }));

    builder.addCase(getDiscountById.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      discountInfo: action.payload,
    }));

    builder.addCase(createDiscountCode.pending, (state) => ({ ...state, loading: true, errors: undefined }));

    builder.addCase(createDiscountCode.fulfilled, (state) => ({
      ...state,
      loading: false,
      showAddModal: false,
    }));

    builder.addCase(createDiscountCode.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));

    builder.addCase(getDiscountCode.pending, (state) => ({ ...state, loading: true }));

    builder.addCase(getDiscountCode.fulfilled, (state) => ({ ...state, loading: false }));

    builder.addCase(changeActivateDiscountCode.pending, (state) => ({ ...state, loading: true }));

    builder.addCase(changeActivateDiscountCode.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(changeActivateDiscountCode.rejected, (state) => ({
      ...state,
      loading: false,
    }));
  },
});

export default discountCodeSlice;
export { changeActivateDiscountCode, createDiscountCode, getAllDiscountCode, getDiscountById, getDiscountCode };
